import React, { useEffect, useState, useCallback } from "react";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { LedgerTable } from "@/components/v3/LedgerTable";
import api from "@/lib/api";
import { formatNumber } from "@/lib/format";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Handshake, BadgeCheck, FileSignature, Sparkles, Search, ChevronLeft, ChevronRight, Download, CheckCircle2, XCircle } from "lucide-react";

const PALETTE = ["#b3122a", "#111111", "#d97706", "#0e7490", "#15803d", "#7c3aed", "#be185d", "#475569"];

const KPI = ({ icon: Icon, label, value, sub }) => (
  <div className="rf-card p-5" data-testid={`dsa-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      <Icon size={18} className="text-[#b3122a]" />
    </div>
    <div className="text-3xl font-extrabold text-[#1a1a1b]">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const ChartCard = ({ title, children, testid }) => (
  <div className="rf-card p-5" data-testid={testid}>
    <h3 className="font-display text-base font-bold text-[#1a1a1b] mb-4">{title}</h3>
    <ResponsiveContainer width="100%" height={240}>{children}</ResponsiveContainer>
  </div>
);

const Select = ({ value, onChange, options, placeholder, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="px-3 py-2.5 rounded-sm border border-slate-300 text-sm bg-white focus:border-[#b3122a] focus:ring-2 focus:ring-[#b3122a]/20 outline-none transition min-w-[150px]">
    <option value="all">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export default function DSA() {
  const [version] = useLiveRefresh();
  const [summary, setSummary] = useState(null);
  const [charts, setCharts] = useState(null);
  const [filters, setFilters] = useState({ statuses: [], relevances: [], sources: [], states: [] });
  const [data, setData] = useState({ items: [], total: 0, page: 1 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [relevance, setRelevance] = useState("all");
  const [source, setSource] = useState("all");
  const [state, setState] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/dsa/summary").then((r) => setSummary(r.data));
    api.get("/dsa/charts").then((r) => setCharts(r.data));
    api.get("/dsa/filters").then((r) => setFilters(r.data));
  }, [version]);

  const fetchList = useCallback(() => {
    setLoading(true);
    api.get("/dsa/list", { params: { search, status, relevance, source, state, page, page_size: 20 } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [search, status, relevance, source, state, page, version]);

  useEffect(() => { const t = setTimeout(fetchList, 300); return () => clearTimeout(t); }, [fetchList]);
  useEffect(() => { setPage(1); }, [search, status, relevance, source, state]);

  const exportCsv = async () => {
    const res = await api.get("/dsa/export", { params: { search, status, relevance, source, state }, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "dsa_network.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(data.total / 20));

  return (
    <div className="p-6 lg:p-8" data-testid="dsa-page">
      <header className="mb-6">
        <div className="flex items-center gap-2.5">
          <Handshake size={26} className="text-[#b3122a]" />
          <h1 className="font-display text-3xl font-extrabold text-[#1a1a1b]">DSA Network</h1>
        </div>
        <p className="text-slate-500 mt-1">Complete Direct Selling Agent base — sourcing partners across India.</p>
      </header>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPI icon={Handshake} label="Total DSAs" value={formatNumber(summary.total)} sub={`${formatNumber(summary.active)} active`} />
          <KPI icon={Sparkles} label="Relevant" value={formatNumber(summary.relevant)} sub={`${summary.relevant_pct}% of base`} />
          <KPI icon={BadgeCheck} label="Approved" value={formatNumber(summary.approved)} sub="DSA status: Approved" />
          <KPI icon={FileSignature} label="E-Signed" value={formatNumber(summary.esigned)} sub="Agreement e-signed" />
        </div>
      )}

      {charts && (
        <div className="grid lg:grid-cols-2 gap-5 mb-6">
          <ChartCard title="Onboarding Status" testid="dsa-chart-status">
            <BarChart data={charts.by_status} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => formatNumber(v)} />
              <Bar dataKey="count" fill="#b3122a" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ChartCard>

          <ChartCard title="Relevance Classification" testid="dsa-chart-relevance">
            <PieChart>
              <Pie data={charts.by_relevance.slice(0, 6)} dataKey="count" nameKey="name" innerRadius={45} outerRadius={85} paddingAngle={2}>
                {charts.by_relevance.slice(0, 6).map((d, i) => <Cell key={d.name} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => formatNumber(v)} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ChartCard>

          <ChartCard title="DSAs by Source" testid="dsa-chart-source">
            <BarChart data={charts.by_source}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => formatNumber(v)} />
              <Bar dataKey="count" fill="#111111" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartCard>

          <ChartCard title="Top States" testid="dsa-chart-state">
            <BarChart data={charts.by_state} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => formatNumber(v)} />
              <Bar dataKey="count" fill="#0e7490" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ChartCard>
        </div>
      )}

      <div className="rf-card p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="dsa-search"
              placeholder="Search name, mobile, email, company…"
              className="w-full pl-10 pr-4 py-2.5 rounded-sm border border-slate-300 text-sm focus:border-[#b3122a] focus:ring-2 focus:ring-[#b3122a]/20 outline-none transition" />
          </div>
          <Select value={relevance} onChange={setRelevance} options={filters.relevances} placeholder="All Relevance" testid="dsa-filter-relevance" />
          <Select value={status} onChange={setStatus} options={filters.statuses} placeholder="All Status" testid="dsa-filter-status" />
          <Select value={source} onChange={setSource} options={filters.sources} placeholder="All Sources" testid="dsa-filter-source" />
          <Select value={state} onChange={setState} options={filters.states} placeholder="All States" testid="dsa-filter-state" />
          <button onClick={exportCsv} data-testid="dsa-export-csv-btn" className="flex items-center gap-1.5 px-3 py-2.5 rounded-sm bg-[#b3122a] text-white text-sm font-semibold hover:bg-[#8c0d20] transition"><Download size={15} /> Export</button>
        </div>
      </div>

      <div className="rf-card overflow-hidden">
        <div className="overflow-x-auto">
          <LedgerTable labels={["DSA", "Mobile", "Email", "Company", "State", "Language", "E-sign", "Relevance", "Status", "Source"]} className="w-full text-sm" data-testid="dsa-table">
            <thead>
              <tr className="bg-[#111111] text-slate-300">
                {["DSA", "Mobile", "Email", "Company", "State", "Language", "E-Sign", "Relevance", "Status", "Source"].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold text-xs uppercase tracking-wide text-left whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400" data-testid="dsa-empty">No DSAs found.</td></tr>}
              {!loading && data.items.map((d) => (
                <tr key={d.id} className="rf-table-row border-t border-slate-100" data-testid={`dsa-row-${d.id}`}>
                  <td className="px-4 py-3 font-semibold text-[#1a1a1b] whitespace-nowrap">{d.name}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap font-mono text-xs">{d.number || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs">{d.email || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{d.company || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{d.state || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{d.language}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {d.esigned
                      ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><CheckCircle2 size={14} /> Yes</span>
                      : <span className="inline-flex items-center gap-1 text-slate-400 text-xs"><XCircle size={14} /> No</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${d.relevance.toLowerCase().startsWith("relevant") ? "bg-emerald-100 text-emerald-700" : d.relevance.toLowerCase().startsWith("blacklist") ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>{d.relevance}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs">{d.status}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{d.source}</td>
                </tr>
              ))}
            </tbody>
          </LedgerTable>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
          <span className="text-sm text-slate-500">Page {data.page} of {totalPages} · {formatNumber(data.total)} DSAs</span>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="dsa-page-prev" className="px-3 py-1.5 rounded-sm border border-slate-300 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition flex items-center gap-1"><ChevronLeft size={16} /> Prev</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="dsa-page-next" className="px-3 py-1.5 rounded-sm border border-slate-300 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition flex items-center gap-1">Next <ChevronRight size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
